import React, { useCallback, useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  buildClientSurveyUrl,
  buildGeneralSurveyUrl,
  buildOwnerSurveyUrl,
  downloadPublicAsset,
  getPresetQrAssets,
  isValidSurveyorCode,
  normalizeSurveyorCode,
  PRESET_SURVEYOR_CODES,
  SURVEY_APP_ORIGIN,
} from '../utils/surveyQrUrls';
import {
  formatSurveyorLabel,
  getSurveyorName,
  getSurveyorRegistry,
  listSurveyors,
  setSurveyorNameAsync,
  approveSurveyorAsync,
  rejectSurveyorAsync,
  deleteSurveyorAsync,
  canDeleteSurveyor,
  suggestNextSurveyorCode,
  syncSurveyorsFromServer,
  setFreelancerStatusAsync,
  countPendingSurveyors,
} from '../utils/surveySurveyorRegistry';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const GOLD = '#C9A84C';
const NAVY = '#14213d';

const QR_OPTS = { errorCorrectionLevel: 'H', margin: 2, width: 512, color: { dark: NAVY, light: '#ffffff' } };

const inputClass = 'w-full max-w-xs rounded-lg border border-de-gold/25 bg-[#0c1a30] px-3 py-2 text-sm text-white uppercase tracking-wider';
const nameInputClass = 'w-full max-w-xs rounded-lg border border-de-gold/25 bg-[#0c1a30] px-3 py-2 text-sm text-white';

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
}

async function downloadPrintCardPdf({ ownerQr, clientQr, surveyorCode, freelancerName }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a6' });
  const w = 148;
  const h = 105;
  doc.setFillColor(20, 33, 61);
  doc.rect(0, 0, w, h, 'F');
  doc.setTextColor(201, 168, 76);
  doc.setFontSize(13);
  doc.text('Development Express', w / 2, 11, { align: 'center' });
  doc.setFontSize(8);
  doc.setTextColor(232, 236, 241);
  doc.text('Construction Machine Survey', w / 2, 17, { align: 'center' });
  doc.addImage(ownerQr, 'PNG', 18, 24, 38, 38);
  doc.addImage(clientQr, 'PNG', 92, 24, 38, 38);
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('Machine Owner', 37, 68, { align: 'center' });
  doc.text('Client Survey', 111, 68, { align: 'center' });
  doc.setFontSize(8);
  doc.setTextColor(201, 168, 76);
  const label = freelancerName
    ? `${freelancerName} (${surveyorCode})`
    : `Surveyor: ${surveyorCode}`;
  doc.text(label, w / 2, 78, { align: 'center' });
  doc.setFontSize(7);
  doc.setTextColor(180, 190, 200);
  doc.text('app.developmentexpress.in', w / 2, 86, { align: 'center' });
  doc.save(`DE-survey-card-${surveyorCode}.pdf`);
}

function QrPreview({ title, subtitle, url, imageSrc, isPreset, onDownload, downloadLabel, emptyLabel, printPackLabel }) {
  return (
    <div
      className="flex flex-col items-center rounded-2xl border p-5"
      style={{ borderColor: `${GOLD}40`, background: `linear-gradient(145deg, ${NAVY}, #0a1628)` }}
    >
      <div className="flex items-center gap-2">
        <h3 className="m-0 text-base font-bold text-de-gold">{title}</h3>
        {isPreset && <Badge className="bg-de-gold/20 text-de-gold">{printPackLabel}</Badge>}
      </div>
      <p className="mt-1 mb-4 text-center text-xs text-de-muted">{subtitle}</p>
      <div className="rounded-xl bg-white p-3">
        {imageSrc ? (
          <img src={imageSrc} alt={`QR — ${title}`} className="h-48 w-48 sm:h-56 sm:w-56" />
        ) : (
          <div className="flex h-48 w-48 items-center justify-center text-sm text-gray-400 sm:h-56 sm:w-56">
            {emptyLabel}
          </div>
        )}
      </div>
      <p className="mt-3 break-all text-center font-mono text-[10px] text-de-muted">{url || '—'}</p>
      <Button
        type="button"
        size="sm"
        className="mt-4 bg-de-gold text-[#14213d] hover:bg-de-gold/90"
        disabled={!imageSrc}
        onClick={onDownload}
      >
        {downloadLabel}
      </Button>
    </div>
  );
}

export default function SurveyQrGeneratorTab({ onPendingCountChange = null }) {
  const { t } = useLanguage();
  const [rawCode, setRawCode] = useState('OM01');
  const [freelancerName, setFreelancerName] = useState('');
  const [upiId, setUpiId] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [saveError, setSaveError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deletingCode, setDeletingCode] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [registryVersion, setRegistryVersion] = useState(0);
  const [ownerQr, setOwnerQr] = useState('');
  const [clientQr, setClientQr] = useState('');
  const [generalQr, setGeneralQr] = useState('');
  const [generating, setGenerating] = useState(false);

  const surveyorCode = useMemo(() => normalizeSurveyorCode(rawCode), [rawCode]);
  const valid = isValidSurveyorCode(surveyorCode);
  const presetAssets = valid ? getPresetQrAssets(surveyorCode) : null;
  const savedFreelancers = useMemo(() => listSurveyors(), [registryVersion]);
  const pendingFreelancers = useMemo(
    () => savedFreelancers.filter((f) => f.name && f.status === 'pending'),
    [savedFreelancers],
  );
  const activeFreelancers = useMemo(
    () => savedFreelancers.filter(
      (f) => f.name && (f.status === 'active' || f.status === 'inactive' || !f.status),
    ),
    [savedFreelancers],
  );
  const applyUrl = `${SURVEY_APP_ORIGIN}/freelancer/apply`;

  const ownerUrl = valid ? buildOwnerSurveyUrl(surveyorCode) : '';
  const clientUrl = valid ? buildClientSurveyUrl(surveyorCode) : '';
  const generalUrl = valid ? buildGeneralSurveyUrl(surveyorCode) : '';

  const ownerImage = presetAssets?.ownerPng || ownerQr;
  const clientImage = presetAssets?.clientPng || clientQr;
  const generalImage = generalQr;
  const trimmedName = freelancerName.trim();
  const useDynamicPrintCard = Boolean(trimmedName) || !presetAssets;

  const refreshRegistry = useCallback(() => {
    setRegistryVersion((v) => v + 1);
    if (onPendingCountChange) {
      onPendingCountChange(countPendingSurveyors());
    }
  }, [onPendingCountChange]);

  const selectFreelancer = useCallback((code, name) => {
    setRawCode(code);
    setFreelancerName(name || getSurveyorName(code));
    setSavedMsg('');
    setSaveError('');
  }, []);

  useEffect(() => {
    let cancelled = false;
    syncSurveyorsFromServer().then(() => {
      if (!cancelled) refreshRegistry();
    });
    return () => { cancelled = true; };
  }, [refreshRegistry]);

  useEffect(() => {
    if (valid) {
      setFreelancerName(getSurveyorName(surveyorCode));
      const entry = getSurveyorRegistry()[surveyorCode] || {};
      setUpiId(entry.upiId || '');
      setBankAccount(entry.bankAccount || '');
      setBankIfsc(entry.bankIfsc || '');
    }
  }, [surveyorCode, valid, registryVersion]);

  const generateQrs = useCallback(async () => {
    if (!valid) {
      setOwnerQr('');
      setClientQr('');
      setGeneralQr('');
      return;
    }
    setGenerating(true);
    try {
      const general = await QRCode.toDataURL(generalUrl, QR_OPTS);
      setGeneralQr(general);
      if (presetAssets) {
        setOwnerQr('');
        setClientQr('');
      } else {
        const [owner, client] = await Promise.all([
          QRCode.toDataURL(ownerUrl, QR_OPTS),
          QRCode.toDataURL(clientUrl, QR_OPTS),
        ]);
        setOwnerQr(owner);
        setClientQr(client);
      }
    } catch (_err) {
      setOwnerQr('');
      setClientQr('');
      setGeneralQr('');
    } finally {
      setGenerating(false);
    }
  }, [valid, presetAssets, ownerUrl, clientUrl, generalUrl]);

  useEffect(() => {
    generateQrs();
  }, [generateQrs]);

  const handleSaveFreelancer = async () => {
    if (!valid || !trimmedName) return;
    setSaveError('');
    setDeleteError('');
    const result = await setSurveyorNameAsync(surveyorCode, trimmedName, {
      upi_id: upiId.trim(),
      bank_account: bankAccount.trim(),
      bank_ifsc: bankIfsc.trim(),
    });
    refreshRegistry();
    if (!result.ok) {
      setSaveError(result.error || 'Save failed');
      setSavedMsg('');
      return;
    }
    setSavedMsg(t('surveyQrFreelancerSaved').replace('{name}', trimmedName).replace('{code}', surveyorCode));
  };

  const handleNewFreelancer = () => {
    const nextCode = suggestNextSurveyorCode();
    setRawCode(nextCode);
    setFreelancerName('');
    setSavedMsg('');
    setSaveError('');
    setDeleteError('');
  };

  const handleApproveFreelancer = async (code) => {
    setActionCode(code);
    setDeleteError('');
    const result = await approveSurveyorAsync(code);
    setActionCode('');
    refreshRegistry();
    if (!result.ok) {
      setDeleteError(result.error || 'Approve failed');
      return;
    }
    setSavedMsg(t('surveyQrFreelancerApproved').replace('{code}', code));
  };

  const handleRejectFreelancer = async (code) => {
    const row = pendingFreelancers.find((f) => f.code === code);
    const confirmMsg = t('surveyQrRejectConfirm')
      .replace('{name}', row?.name || code)
      .replace('{code}', code);
    if (!window.confirm(confirmMsg)) return;
    setActionCode(code);
    setDeleteError('');
    const result = await rejectSurveyorAsync(code);
    setActionCode('');
    refreshRegistry();
    if (!result.ok) {
      setDeleteError(result.error || 'Reject failed');
    }
  };

  const handleDeleteFreelancer = async (code) => {
    if (!canDeleteSurveyor(code)) return;
    const name = getSurveyorName(code);
    const confirmMsg = t('surveyQrDeleteConfirm')
      .replace('{name}', name)
      .replace('{code}', code);
    if (!window.confirm(confirmMsg)) return;
    setDeleteError('');
    setDeletingCode(code);
    const result = await deleteSurveyorAsync(code);
    setDeletingCode('');
    refreshRegistry();
    if (!result.ok) {
      setDeleteError(result.error || 'Delete failed');
      return;
    }
    setSavedMsg(t('surveyQrFreelancerDeleted').replace('{name}', name).replace('{code}', code));
    if (surveyorCode === code) {
      setRawCode(suggestNextSurveyorCode());
      setFreelancerName('');
    }
  };

  const handleToggleBlockFreelancer = async (code, currentlyBlocked) => {
    const nextStatus = currentlyBlocked ? 'active' : 'inactive';
    const confirmMsg = currentlyBlocked
      ? t('surveyQrUnblockConfirm').replace('{code}', code)
      : t('surveyQrBlockConfirm').replace('{code}', code);
    if (!window.confirm(confirmMsg)) return;
    setActionCode(code);
    setDeleteError('');
    const result = await setFreelancerStatusAsync(code, nextStatus);
    setActionCode('');
    refreshRegistry();
    if (!result.ok) {
      setDeleteError(result.error || 'Status update failed');
      return;
    }
    setSavedMsg(
      currentlyBlocked
        ? t('surveyQrFreelancerUnblocked').replace('{code}', code)
        : t('surveyQrFreelancerBlocked').replace('{code}', code),
    );
  };

  const handlePrintCard = async () => {
    if (useDynamicPrintCard) {
      let oQr = ownerQr;
      let cQr = clientQr;
      if (presetAssets && (!oQr || !cQr)) {
        const [owner, client] = await Promise.all([
          QRCode.toDataURL(ownerUrl, QR_OPTS),
          QRCode.toDataURL(clientUrl, QR_OPTS),
        ]);
        oQr = owner;
        cQr = client;
      }
      if (oQr && cQr) {
        downloadPrintCardPdf({
          ownerQr: oQr,
          clientQr: cQr,
          surveyorCode,
          freelancerName: trimmedName,
        });
      }
      return;
    }
    if (presetAssets) {
      downloadPublicAsset(presetAssets.printCardPdf, `DE-survey-card-${surveyorCode}.pdf`);
    }
  };

  const handleOwnerDownload = () => {
    if (presetAssets) {
      downloadPublicAsset(presetAssets.ownerPng, `qr-owner-${surveyorCode}.png`);
      return;
    }
    downloadDataUrl(ownerQr, `qr-owner-${surveyorCode}.png`);
  };

  const handleClientDownload = () => {
    if (presetAssets) {
      downloadPublicAsset(presetAssets.clientPng, `qr-client-${surveyorCode}.png`);
      return;
    }
    downloadDataUrl(clientQr, `qr-client-${surveyorCode}.png`);
  };

  const handleGeneralDownload = () => {
    if (!generalQr) return;
    downloadDataUrl(generalQr, `qr-general-${surveyorCode}.png`);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-de-gold/20 bg-[#0c1a30]/80 p-4 text-sm text-de-muted">
        <p className="m-0 font-semibold text-de-gold">{t('surveyQrApplyLinkTitle')}</p>
        <p className="mt-2 m-0">{t('surveyQrApplyLinkHint')}</p>
        <p className="mt-2 mb-0 break-all font-mono text-xs text-white/90">{applyUrl}</p>
      </section>

      <section className="rounded-xl border border-amber-400/25 bg-amber-950/20 p-4">
        <p className="m-0 mb-3 text-xs font-bold uppercase tracking-wider text-amber-300">
          {t('surveyQrPendingApproval')} ({pendingFreelancers.length})
        </p>
        {pendingFreelancers.length === 0 ? (
          <p className="m-0 text-sm text-de-muted">{t('surveyQrNoPending')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('surveyQrFreelancerName')}</TableHead>
                <TableHead>{t('surveyQrUniqueId')}</TableHead>
                <TableHead>{t('surveyQrMobile')}</TableHead>
                <TableHead className="text-right">{t('surveyQrFreelancerActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingFreelancers.map((f) => (
                <TableRow key={f.code}>
                  <TableCell className="font-medium text-white">{f.name}</TableCell>
                  <TableCell><Badge className="bg-amber-400/20 font-mono text-amber-200">{f.code}</Badge></TableCell>
                  <TableCell className="text-de-muted">{f.phone || '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        type="button"
                        size="sm"
                        className="bg-emerald-600 text-white hover:bg-emerald-500"
                        disabled={actionCode === f.code}
                        onClick={() => handleApproveFreelancer(f.code)}
                      >
                        {actionCode === f.code ? t('surveyQrApproving') : t('surveyQrApproveFreelancer')}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-red-400/40 text-red-300"
                        disabled={actionCode === f.code}
                        onClick={() => handleRejectFreelancer(f.code)}
                      >
                        {t('surveyQrRejectFreelancer')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      <section
        className="rounded-2xl border p-4 sm:p-5"
        style={{ borderColor: `${GOLD}30`, background: 'rgba(201,168,76,0.04)' }}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold uppercase tracking-wider text-de-gold">{t('surveyQrRegistrationTitle')}</h3>
            <p className="mt-1 text-sm text-de-muted">{t('surveyQrRegistrationHint')}</p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-de-gold/40 text-de-gold"
            onClick={handleNewFreelancer}
          >
            {t('surveyQrNewFreelancer')}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {PRESET_SURVEYOR_CODES.map((code) => (
            <Button
              key={code}
              type="button"
              size="sm"
              variant={surveyorCode === code ? 'default' : 'outline'}
              className={surveyorCode === code ? 'bg-de-gold text-[#14213d]' : 'border-de-gold/40 text-de-gold'}
              onClick={() => selectFreelancer(code, getSurveyorName(code))}
            >
              {formatSurveyorLabel(code)}
            </Button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyQrUniqueId')}
            <input
              className={inputClass}
              value={rawCode}
              onChange={(e) => setRawCode(e.target.value)}
              placeholder="FR01"
              maxLength={12}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyQrFreelancerName')}
            <input
              className={nameInputClass}
              value={freelancerName}
              onChange={(e) => setFreelancerName(e.target.value)}
              placeholder="Yashraj Patil"
              maxLength={80}
              autoComplete="off"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyQrUpiId')}
            <input
              className={nameInputClass}
              value={upiId}
              onChange={(e) => setUpiId(e.target.value)}
              placeholder="name@upi"
              maxLength={80}
              autoComplete="off"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyQrBankAccount')}
            <input
              className={nameInputClass}
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value)}
              placeholder="Account number"
              maxLength={40}
              autoComplete="off"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyQrBankIfsc')}
            <input
              className={nameInputClass}
              value={bankIfsc}
              onChange={(e) => setBankIfsc(e.target.value)}
              placeholder="IFSC"
              maxLength={20}
              autoComplete="off"
            />
          </label>
          <Button
            type="button"
            size="sm"
            className="bg-de-gold text-[#14213d] hover:bg-de-gold/90"
            disabled={!valid || !trimmedName}
            onClick={handleSaveFreelancer}
          >
            {t('surveyQrSaveFreelancer')}
          </Button>
        </div>
        <p className="mt-2 text-xs text-de-muted">{t('surveyQrFreelancerNameHint')}</p>
        {valid && trimmedName && (
          <p className="mt-2 text-sm text-white/90">
            {t('surveyQrFreelancerPreview').replace('{name}', trimmedName).replace('{code}', surveyorCode)}
          </p>
        )}
        {surveyorCode && !valid && (
          <p className="mt-2 text-sm text-de-accent">{t('surveyQrCodeInvalid')}</p>
        )}
        {valid && (
          <p className="mt-2 text-sm text-emerald-400/90">
            {t('surveyQrCodeActive').replace('{code}', surveyorCode)}
            {presetAssets && !trimmedName ? t('surveyQrOfficialPack') : generating ? t('surveyQrGenerating') : ''}
          </p>
        )}
        {savedMsg && <p className="mt-2 text-sm text-de-gold">{savedMsg}</p>}
        {saveError && <p className="mt-2 text-sm text-de-accent">{saveError}</p>}
        {deleteError && <p className="mt-2 text-sm text-de-accent">{deleteError}</p>}
      </section>

      <section className="rounded-xl border border-de-gold/15 bg-[#0c1a30]/60 p-4">
          <p className="m-0 mb-3 text-xs font-bold uppercase tracking-wider text-de-gold">
            {t('surveyQrActiveFreelancers')} ({activeFreelancers.length})
          </p>
          {activeFreelancers.length === 0 ? (
            <p className="m-0 text-sm text-de-muted">{t('surveyQrNoActiveFreelancers')}</p>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('surveyQrFreelancerName')}</TableHead>
                <TableHead>{t('surveyQrUniqueId')}</TableHead>
                <TableHead>{t('surveyQrFreelancerStatus')}</TableHead>
                <TableHead className="text-right">{t('surveyQrFreelancerActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeFreelancers.map((f) => (
                <TableRow
                  key={f.code}
                  className={surveyorCode === f.code ? 'bg-de-gold/10' : undefined}
                >
                  <TableCell className="font-medium text-white">{f.name}</TableCell>
                  <TableCell>
                    <Badge className="bg-de-gold/20 font-mono text-de-gold">{f.code}</Badge>
                  </TableCell>
                  <TableCell>
                    {f.status === 'inactive' ? (
                      <Badge className="bg-red-950/50 text-red-300">{t('surveyQrStatusBlocked')}</Badge>
                    ) : (
                      <Badge className="bg-emerald-950/50 text-emerald-300">{t('surveyQrStatusActive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-de-gold/30 text-de-gold"
                        onClick={() => selectFreelancer(f.code, f.name)}
                      >
                        {t('surveyQrFreelancerOpen')}
                      </Button>
                      {f.status !== 'pending' && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={f.status === 'inactive' ? 'border-emerald-400/40 text-emerald-300' : 'border-amber-400/40 text-amber-200'}
                          disabled={actionCode === f.code}
                          onClick={() => handleToggleBlockFreelancer(f.code, f.status === 'inactive')}
                        >
                          {f.status === 'inactive' ? t('surveyQrUnblockFreelancer') : t('surveyQrBlockFreelancer')}
                        </Button>
                      )}
                      {canDeleteSurveyor(f.code) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-red-400/40 text-red-300 hover:bg-red-950/40"
                          disabled={deletingCode === f.code}
                          onClick={() => handleDeleteFreelancer(f.code)}
                        >
                          {deletingCode === f.code ? t('surveyQrDeleting') : t('surveyQrDeleteFreelancer')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
          <p className="mt-3 text-xs text-de-muted">{t('surveyQrDeleteHint')}</p>
        </section>

      <section>
        <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-de-gold">{t('surveyQrSectionTitle')}</h3>
        <p className="mb-4 text-sm text-de-muted">{t('surveyQrCodeHint').replace('{origin}', SURVEY_APP_ORIGIN)}</p>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <QrPreview
          title={t('surveyQrOwnerTitle')}
          subtitle={t('surveyQrOwnerSurvey')}
          url={ownerUrl}
          imageSrc={ownerImage}
          isPreset={Boolean(presetAssets && !trimmedName)}
          downloadLabel={t('surveyQrDownloadOwnerPng')}
          emptyLabel={t('surveyQrEnterCode')}
          printPackLabel={t('surveyQrPrintPack')}
          onDownload={handleOwnerDownload}
        />
        <QrPreview
          title={t('surveyQrClientTitle')}
          subtitle={t('surveyQrClientSurvey')}
          url={clientUrl}
          imageSrc={clientImage}
          isPreset={Boolean(presetAssets && !trimmedName)}
          downloadLabel={t('surveyQrDownloadClientPng')}
          emptyLabel={t('surveyQrEnterCode')}
          printPackLabel={t('surveyQrPrintPack')}
          onDownload={handleClientDownload}
        />
        <QrPreview
          title="General Survey"
          subtitle="General (Owner or Client selects)"
          url={generalUrl}
          imageSrc={generalImage}
          isPreset={false}
          downloadLabel="Download General PNG"
          emptyLabel={t('surveyQrEnterCode')}
          printPackLabel={t('surveyQrPrintPack')}
          onDownload={handleGeneralDownload}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          variant="outline"
          className="border-de-gold/40 text-de-gold"
          disabled={!ownerImage || !clientImage}
          onClick={handlePrintCard}
        >
          {t('surveyQrDownloadPrintCard')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="border-de-gold/40 text-de-gold"
          disabled={!ownerImage}
          onClick={handleOwnerDownload}
        >
          {t('surveyQrOwnerPng')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="border-de-gold/40 text-de-gold"
          disabled={!clientImage}
          onClick={handleClientDownload}
        >
          {t('surveyQrClientPng')}
        </Button>
      </div>
      </section>

      <section className="rounded-xl border border-de-gold/15 bg-[#0c1a30]/60 p-4 text-xs text-de-muted">
        <p className="m-0 font-semibold text-de-gold">{t('surveyQrPresetTitle')}</p>
        <ul className="mt-2 list-inside list-disc space-y-1">
          <li>{t('surveyQrPresetOfficial')}</li>
          <li>{t('surveyQrPresetLive')}</li>
          <li>{t('surveyQrTrackingHint')}</li>
        </ul>
      </section>
    </div>
  );
}
